import React from 'react';
import { Chip } from '../../Components/Others';
import { TextButton } from '../../Components/Buttons';

export const TRANSACTION_TAB_TITLES = [
    { id: 1, label: 'Switch' },
    { id: 2, label: 'SWP' },
    { id: 3, label: 'STP' },
];

export const TAB_TITLES = TRANSACTION_TAB_TITLES;

// NFO is an Orders submenu only. It is not a Switch/SWP/STP order type.
export const NFO_TAB_ID = 4;
export const NFO_INVESTMENT_TYPE = 6;

export const ORDER_TYPE_BY_TAB = {
    1: 6,
    2: 4,
    3: 5,
};

// SWP, STP, Switch — handled in Switch/SWP/STP menu and customer tab
export const EXCLUDED_ORDER_TYPE_FILTER_VALUES = [4, 5, 6];

export const isNfoTab = (tabId) => tabId === NFO_TAB_ID;

export const isSchemeFilterTab = (tabId) => tabId === 2;

export const getListInvestmentType = (tabId) => (isNfoTab(tabId) ? NFO_INVESTMENT_TYPE : null);

export const getListOrderType = (tabId) => {
    if (isNfoTab(tabId)) return [];
    const orderType = ORDER_TYPE_BY_TAB[tabId];
    return orderType == null ? [] : [orderType];
};

export const filterOrderTypeOptions = (orderTypes = []) => {
    return orderTypes.filter((item) => !EXCLUDED_ORDER_TYPE_FILTER_VALUES.includes(item.value));
};

export const getActiveTransactionTab = (tabId) => {
    if (isNfoTab(tabId)) return NFO_TAB_ID;
    const tab = TRANSACTION_TAB_TITLES.find((item) => item.id === tabId);
    if (!tab || tab.disabled) {
        return 1;
    }
    return tabId;
};

const displayValue = (value) => {
    if (value === null || value === undefined || value === "" || value === "-") {
        return "-";
    }
    return value;
};

const th = (label) => <span className="e-table-th-wrap">{label}</span>;
const thLines = (...lines) => (
    <span className="e-table-th-wrap">
        {lines.map((line, i) => (
            <React.Fragment key={i}>
                {i > 0 ? <br /> : null}
                {line}
            </React.Fragment>
        ))}
    </span>
);

const getStatusChipClass = (status) => {
    if (!status) return "e-bg-bleu-de-france";
    const normalized = status.toLowerCase();
    if (normalized === "success" || normalized === "successful") return "e-bg-green-crayola";
    if (normalized === "failed") return "e-bg-maximum-red";
    if (normalized === "pending"|| normalized === "in progress") return "e-bg-marigold";
    return "e-bg-bleu-de-france";
};

const getOutlineStatusChipClass = (status) => {
    if (!status) return "e-text-bleu-de-france e-border-1px-solid-bleu-de-france";
    const normalized = status.toLowerCase();
    if (normalized === "success" || normalized === "successful") return "e-text-green-crayola e-border-1px-green-crayola";
    if (normalized === "failed") return "e-text-maximum-red e-border-1px-maximum-red";
    if (normalized === "pending"|| normalized === "in progress") return "e-text-marigold e-border-1px-marigold";
    return "e-text-bleu-de-france e-border-1px-solid-bleu-de-france";
};

const COLUMN_WIDTHS = {
    orderId: "130px",
    fpOrderId: "320px",
    customerId: "130px",
    customerName: "280px",
    orderDate: "180px",
    type: "120px",
    orderType: "100px",
    product: "140px",
    productName: "240px",
    switchOut: "300px",
    switchIn: "300px",
    orderAmount: "150px",
    switchAmount: "150px",
    allocatedAmount: "150px",
    redeemedUnits: "140px",
    redeemedNav: "140px",
    purchaseNav: "140px",
    paymentStatus: "150px",
    orderStatus: "160px",
    fpPaymentStatus: "150px",
    fpOrderStatus: "160px",
    failureReason: "280px",
    folio: "160px",
    allocatedDate: "130px",
    navDate: "130px",
    orderStatusUpdatedOn: "180px",
};

export const TransactionExpandableRows = ({ data }) => {
    const bgClass = data._index % 2 === 0 ? 'e-bg-magnolia-dark' : 'e-bg-magnolia-extra-dark';

    if (!data.data || data.data.length === 0) {
        return <div className={`${bgClass} p-3 e-text-black e-primary-font e-font-14 e-font-weight-400 line-height-16px`}>No schemes available.</div>;
    }

    const renderCell = (width, value, columnId) => (
        <p
            className='mb-0 e-text-smoky-black e-alt-font e-font-14 e-font-weight-400 line-height-24px text-truncate'
            style={{ width }}
            data-column-id={columnId}
            title={value || undefined}
        >
            {displayValue(value)}
        </p>
    );

    const renderStatusCell = (width, status, columnId) => (
        <div style={{ width }} data-column-id={columnId}>
            {status ?
                <Chip label={status}
                    textAlign="justify-content-start"
                    className={`${getOutlineStatusChipClass(status)} bg-transparent`} />
                : "-"}
        </div>
    );

    return (
        <div className={`py-3 padding-30px-lr d-flex flex-column gap-12px ${bgClass}`}>
            {data.data.map((child_data, index) => (
                <div key={index} className='d-flex align-items-center'>
                    {renderCell(COLUMN_WIDTHS.orderId, child_data.order_display_id, "tx_order_id")}
                    {renderCell(COLUMN_WIDTHS.fpOrderId, child_data.fp_order_id, "tx_fp_order_id")}
                    {renderCell(COLUMN_WIDTHS.customerId, child_data.customer_display_id, "tx_customer_id")}
                    {renderCell(COLUMN_WIDTHS.customerName, child_data.name, "tx_customer_name")}
                    {renderCell(COLUMN_WIDTHS.orderDate, child_data.order_date, "tx_order_date")}
                    {renderCell(COLUMN_WIDTHS.type, child_data.type, "tx_type")}
                    <div style={{ width: COLUMN_WIDTHS.orderType }} data-column-id="tx_order_type">
                        <Chip label={"Child"}
                            textAlign="justify-content-start"
                            className={"bg-transparent e-text-green-crayola e-border-1px-green-crayola "} />
                    </div>
                    {renderCell(COLUMN_WIDTHS.product, child_data.product, "tx_product")}
                    {renderCell(COLUMN_WIDTHS.productName, child_data.product_name, "tx_product_name")}
                    {renderCell(COLUMN_WIDTHS.switchOut, child_data.switch_out_scheme_name || child_data.scheme_name, "tx_switch_out")}
                    {renderCell(COLUMN_WIDTHS.switchIn, child_data.switch_in_scheme_name, "tx_switch_in")}
                    {renderCell(COLUMN_WIDTHS.orderAmount, child_data.amount_text, "tx_order_amount")}
                    {renderCell(COLUMN_WIDTHS.switchAmount, child_data.switch_amount, "tx_switch_amount")}
                    {renderCell(COLUMN_WIDTHS.allocatedAmount, child_data.allocated_amount, "tx_allocated_amount")}
                    {renderCell(COLUMN_WIDTHS.redeemedUnits, child_data.redeemed_units, "tx_redeemed_units")}
                    {renderCell(COLUMN_WIDTHS.redeemedNav, child_data.redeemed_nav, "tx_redeemed_nav")}
                    {renderCell(COLUMN_WIDTHS.purchaseNav, child_data.purchase_nav, "tx_purchase_nav")}
                    {renderStatusCell(COLUMN_WIDTHS.paymentStatus, child_data.payment_status, "tx_payment_status")}
                    {renderStatusCell(COLUMN_WIDTHS.orderStatus, child_data.order_status, "tx_order_status")}
                    {renderStatusCell(COLUMN_WIDTHS.fpPaymentStatus, child_data.fp_payment_status, "tx_fp_payment_status")}
                    {renderStatusCell(COLUMN_WIDTHS.fpOrderStatus, child_data.fp_order_status, "tx_fp_order_status")}
                    {renderCell(COLUMN_WIDTHS.failureReason, child_data.failure_reason, "tx_failure_reason")}
                    {renderCell(COLUMN_WIDTHS.folio, child_data.folio_no, "tx_folio")}
                    {renderCell(COLUMN_WIDTHS.allocatedDate, child_data.allocated_date, "tx_allocated_date")}
                    {renderCell(COLUMN_WIDTHS.navDate, child_data.nav_date, "tx_nav_date")}
                    {renderCell(COLUMN_WIDTHS.orderStatusUpdatedOn, child_data.order_status_updated_on, "tx_status_updated_on")}
                </div>
            ))}
        </div>
    );
};

export const getTransactionColumns = ({ onCustomerClick } = {}) => [
    {
        name: 'Order ID',
        selector: row => row.order_display_id || "-",
        cell: row => <span className="text-truncate d-block" title={row.order_display_id || undefined}>{row.order_display_id || "-"}</span>,
        width: COLUMN_WIDTHS.orderId,
        id: "tx_order_id",
        sortable: true,
        sortFunction: (a, b) => {
            if (a.order_id < b.order_id) return -1;
            if (a.order_id > b.order_id) return 1;
            return 0;
        }
    },
    {
        name: 'FP Order ID',
        selector: row => row.fp_order_id ? row.fp_order_id : "-",
        cell: row => <span className="text-truncate d-block" title={row.fp_order_id || undefined}>{row.fp_order_id ? row.fp_order_id : "-"}</span>,
        width: COLUMN_WIDTHS.fpOrderId,
        id: "tx_fp_order_id",
    },
    {
        name: 'Customer ID',
        selector: row => row.customer_display_id || "-",
        cell: row => <span className="text-truncate d-block" title={row.customer_display_id || undefined}>{row.customer_display_id || "-"}</span>,
        width: COLUMN_WIDTHS.customerId,
        id: "tx_customer_id",
        sortable: true,
        sortFunction: (a, b) => {
            if (a.customer_id < b.customer_id) return -1;
            if (a.customer_id > b.customer_id) return 1;
            return 0;
        }
    },
    {
        name: 'Customer Name',
        cell: (row) =>
            <div className='text-truncate' onClick={(e) => e.stopPropagation()} title={row.name || undefined}>
                {row.name !== null && onCustomerClick ?
                    <span className='e-text-blue-violet text-decoration-hover-underline cursor-pointer text-truncate d-block'
                        title={row.name}
                        onClick={() => onCustomerClick(row.customer_id)}>
                        {row.name}
                    </span>
                    : displayValue(row.name)}
            </div>,
        width: COLUMN_WIDTHS.customerName,
        id: "tx_customer_name",
        selector: row => row.name,
        sortable: true,
    },
    {
        name: 'Order Date',
        selector: row => row.order_date,
        sortable: true,
        format: row => row.order_date,
        width: COLUMN_WIDTHS.orderDate,
        id: "tx_order_date",
    },
    {
        name: 'Type',
        selector: row => row.type || "-",
        width: COLUMN_WIDTHS.type,
        id: "tx_type",
    },
    {
        name: 'Order Type',
        cell: row => <Chip label={"Parent"}
            className={"e-bg-green-crayola"} />,
        width: COLUMN_WIDTHS.orderType,
        id: "tx_order_type",
    },
    {
        name: 'Product',
        selector: row => row.product || "-",
        width: COLUMN_WIDTHS.product,
        id: "tx_product",
    },
    {
        name: 'Product Name',
        cell: row => <div className="text-truncate" title={row.product_name || undefined}>{row.product_name || "-"}</div>,
        width: COLUMN_WIDTHS.productName,
        id: "tx_product_name",
    },
    {
        name: 'Switch Out',
        cell: row => <div className="text-truncate" title={displayValue(row.switch_out_scheme_name || row.scheme_name)}>{displayValue(row.switch_out_scheme_name || row.scheme_name)}</div>,
        width: COLUMN_WIDTHS.switchOut,
        id: "tx_switch_out",
    },
    {
        name: 'Switch In',
        cell: row => <div className="text-truncate" title={displayValue(row.switch_in_scheme_name)}>{displayValue(row.switch_in_scheme_name)}</div>,
        width: COLUMN_WIDTHS.switchIn,
        id: "tx_switch_in",
    },
    {
        name: 'Order Amount',
        selector: row => row.amount_text || "-",
        width: COLUMN_WIDTHS.orderAmount,
        id: "tx_order_amount",
        sortable: true,
        sortFunction: (a, b) => {
            if (a.amount < b.amount) return -1;
            if (a.amount > b.amount) return 1;
            return 0;
        }
    },
    {
        name: 'Switch Amount',
        selector: row => displayValue(row.switch_amount),
        width: COLUMN_WIDTHS.switchAmount,
        id: "tx_switch_amount",
    },
    {
        name: 'Allocated Amount',
        selector: row => displayValue(row.allocated_amount),
        width: COLUMN_WIDTHS.allocatedAmount,
        id: "tx_allocated_amount",
    },
    {
        name: 'Redeemed Units',
        selector: row => displayValue(row.redeemed_units),
        width: COLUMN_WIDTHS.redeemedUnits,
        id: "tx_redeemed_units",
    },
    {
        name: 'Redeemed NAV',
        selector: row => displayValue(row.redeemed_nav),
        width: COLUMN_WIDTHS.redeemedNav,
        id: "tx_redeemed_nav",
    },
    {
        name: 'Purchase NAV',
        selector: row => displayValue(row.purchase_nav),
        width: COLUMN_WIDTHS.purchaseNav,
        id: "tx_purchase_nav",
    },
    {
        name: 'Rayi Payment Status',
        cell: row => <>
            {row.payment_status ?
                <Chip label={row.payment_status}
                    className={getStatusChipClass(row.payment_status)} />
                : "-"}
        </>,
        width: COLUMN_WIDTHS.paymentStatus,
        id: "tx_payment_status",
    },
    {
        name: thLines('Rayi Order', 'Status'),
        cell: row => <>
            {row.order_status ?
                <Chip label={row.order_status}
                    className={getStatusChipClass(row.order_status)} />
                : "-"}
        </>,
        width: COLUMN_WIDTHS.orderStatus,
        id: "tx_order_status",
    },
    {
        name: 'FP Payment Status',
        cell: row => <>
            {row.fp_payment_status ?
                <Chip label={row.fp_payment_status}
                    className={getStatusChipClass(row.fp_payment_status)} />
                : "-"}
        </>,
        width: COLUMN_WIDTHS.fpPaymentStatus,
        id: "tx_fp_payment_status",
    },
    {
        name: thLines('FP Order', 'Status'),
        cell: row => <>
            {row.fp_order_status ?
                <Chip label={row.fp_order_status}
                    className={getStatusChipClass(row.fp_order_status)} />
                : "-"}
        </>,
        width: COLUMN_WIDTHS.fpOrderStatus,
        id: "tx_fp_order_status",
    },
    {
        name: 'Failed Reason',
        cell: row => <div className="text-truncate" title={displayValue(row.failure_reason)}>{displayValue(row.failure_reason)}</div>,
        width: COLUMN_WIDTHS.failureReason,
        id: "tx_failure_reason",
    },
    {
        name: 'Folio',
        selector: row => row.folio_no || "-",
        width: COLUMN_WIDTHS.folio,
        id: "tx_folio",
    },
    {
        name: 'Allocated Date',
        selector: row => displayValue(row.allocated_date),
        width: COLUMN_WIDTHS.allocatedDate,
        id: "tx_allocated_date",
    },
    {
        name: 'NAV Date',
        selector: row => displayValue(row.nav_date),
        width: COLUMN_WIDTHS.navDate,
        id: "tx_nav_date",
    },
    {
        name: 'Order status updated on',
        selector: row => displayValue(row.order_status_updated_on),
        width: COLUMN_WIDTHS.orderStatusUpdatedOn,
        id: "tx_status_updated_on",
    },
];

export const getOtherTransactionsListColumns = ({
    includeCustomerColumns = true,
    onCustomerClick,
    onViewDetails,
    tabId,
} = {}) => {
    const columns = [
        {
            name: 'Order Date',
            selector: row => row.order_date || "-",
            cell: row =>
                <div className='line-height-20px'>
                    <p className='mb-0 e-font-weight-500'>
                        {row.date || "-"}
                    </p>
                    <p className='mb-0 '>
                        {row.time || ""}
                    </p>
                </div>,
            width: "120px",
            sortable: true,
            id: "order_date",
        },
    ];

    if (includeCustomerColumns) {
        if (tabId !== 3) {
            columns.push({
                name: 'Customer ID',
                selector: row => row.customer_display_id || "-",
                cell: row => <div className='e-font-weight-500'>{row.customer_display_id || "-"}</div>,
                width: "100px",
                sortable: true,
                sortFunction: (a, b) => a.customer_id - b.customer_id,
                id: "customer_id",
            });
        }

        columns.push({
            name: 'Customer Name',
            selector: row => row.name || "-",
            cell: row => (
                <div className='line-height-20px text-truncate' onClick={(event) => event.stopPropagation()} title={row.name || undefined}>
                    {row.name && onCustomerClick ?
                        <span className='e-text-blue-violet text-decoration-hover-underline cursor-pointer text-truncate d-block'
                            title={row.name}
                            onClick={() => onCustomerClick(row.customer_id)}>
                            {row.name}
                        </span>
                        : <span className='text-truncate d-block' title={row.name || undefined}>{displayValue(row.name)}</span>}
                </div>
            ),
            width: "180px",
            sortable: true,
            id: "customer_name",
        });
    }

    if (tabId === 2) {
        columns.push(
            {
                name: 'Scheme',
                selector: row => row.scheme_name || "-",
                cell: row => <div className='line-height-20px' title={row.scheme_name || "-"}>{row.scheme_name || "-"}</div>,
                width: "250px",
                id: "scheme",
            },
            {
                name: thLines('Executed / Total', 'SWPs'),
                selector: row => row.installment_text || "-",
                cell: row => <div>{row.installment_text || "-"}</div>,
                width: "90px",
                id: "executed_swps",
            },
            {
                name: 'SWP Amount',
                selector: row => row.amount_text || "-",
                cell: row => <div>{row.amount_text || "-"}</div>,
                width: "120px",
                sortable: true,
                sortFunction: (a, b) => a.amount - b.amount,
                id: "swp_amount",
            },
            {
                name: thLines('Rayi Order', 'Status'),
                cell: row => row.order_status ?
                    <Chip label={row.order_status}
                        className={getStatusChipClass(row.order_status)} />
                    : "-",
                width: "130px",
                id: "rayi_order_status",
            },
            {
                name: thLines('FP Order', 'Status'),
                cell: row => row.fp_order_status ?
                    <Chip label={row.fp_order_status}
                        className={getStatusChipClass(row.fp_order_status)} />
                    : "-",
                width: "130px",
                id: "fp_order_status",
            },
            {
                name: 'Rayi Order ID',
                selector: row => row.order_display_id || "-",
                cell: row => <div className='line-height-20px'>{row.order_display_id || "-"}</div>,
                width: "134px",
                sortable: true,
                sortFunction: (a, b) => a.order_id - b.order_id,
                id: "rayi_order_id",
            },
        );
    } else if (tabId === 3) {
        columns.push(
            {
                name: 'From Scheme',
                selector: row => row.scheme_name || "-",
                cell: row => <div className='line-height-20px' title={row.scheme_name || "-"}>{row.scheme_name || "-"}</div>,
                width: "250px",
                id: "from_scheme",
            },
            {
                name: 'To Scheme',
                selector: row => row.switch_in_scheme_name || "-",
                cell: row => <div className='line-height-20px' title={row.switch_in_scheme_name || "-"}>{row.switch_in_scheme_name || "-"}</div>,
                width: "250px",
                id: "to_scheme",
            },
            {
                name: thLines('Executed / Total', 'STPs'),
                selector: row => row.installment_text || "-",
                cell: row => <div>{row.installment_text || "-"}</div>,
                width: "90px",
                id: "executed_stps",
            },
            {
                name: 'STP Amount',
                selector: row => row.amount_text || "-",
                cell: row => <div>{row.amount_text || "-"}</div>,
                width: "120px",
                sortable: true,
                sortFunction: (a, b) => a.amount - b.amount,
                id: "stp_amount",
            },
            {
                name: thLines('Rayi Order', 'Status'),
                cell: row => row.order_status ?
                    <Chip label={row.order_status}
                        className={getStatusChipClass(row.order_status)} />
                    : "-",
                width: "130px",
                id: "rayi_order_status",
            },
            {
                name: thLines('FP Order', 'Status'),
                cell: row => row.fp_order_status ?
                    <Chip label={row.fp_order_status}
                        className={getStatusChipClass(row.fp_order_status)} />
                    : "-",
                width: "130px",
                id: "fp_order_status",
            },
            {
                name: 'Rayi Order ID',
                selector: row => row.order_display_id || "-",
                cell: row => <div className='line-height-20px'>{row.order_display_id || "-"}</div>,
                width: "134px",
                sortable: true,
                sortFunction: (a, b) => a.order_id - b.order_id,
                id: "rayi_order_id",
            },
        );
    } else if (tabId === NFO_TAB_ID) {
        const investmentType = (row) => (typeof row.type === "string" && row.type ? row.type : "-");

        columns.push(
            {
                name: 'Scheme',
                selector: row => row.scheme_name || "-",
                cell: row => <div className='line-height-20px text-break'>{row.scheme_name || "-"}</div>,
                width: "250px",
                id: "nfo_scheme",
            },
            {
                name: th('Investment Type'),
                selector: row => investmentType(row),
                cell: row => <div className='line-height-20px text-truncate' title={investmentType(row)}>{investmentType(row)}</div>,
                width: "140px",
                id: "nfo_investment_type",
            },
            {
                name: 'Amount',
                selector: row => row.amount_text || "-",
                cell: row => <div>{row.amount_text || "-"}</div>,
                width: "120px",
                sortable: true,
                sortFunction: (a, b) => (a.amount || 0) - (b.amount || 0),
                id: "nfo_amount",
            },
            {
                name: th('NFO Close Date'),
                selector: row => row.nfo_close_date || "-",
                cell: row => <div className='line-height-20px'>{row.nfo_close_date || "-"}</div>,
                width: "140px",
                id: "nfo_close_date",
            },
            {
                name: th('Payment Status'),
                cell: row => row.payment_status ?
                    <Chip label={row.payment_status}
                        className={getStatusChipClass(row.payment_status)} />
                    : "-",
                width: "140px",
                id: "nfo_payment_status",
            },
            {
                name: th('Order Status'),
                cell: row => row.order_status ?
                    <Chip label={row.order_status}
                        className={getStatusChipClass(row.order_status)} />
                    : "-",
                width: "160px",
                id: "nfo_order_status",
            },
        );
    } else {
        columns.push(
            {
                name: 'From Scheme',
                selector: row => row.switch_out_scheme_name || row.scheme_name || "-",
                cell: row => <div className='line-height-20px' title={row.switch_out_scheme_name || row.scheme_name || "-"}>{row.switch_out_scheme_name || row.scheme_name || "-"}</div>,
                width: "250px",
                id: "from_scheme",
            },
            {
                name: 'To Scheme',
                selector: row => row.switch_in_scheme_name || row.to_scheme || "-",
                cell: row => <div title={row.switch_in_scheme_name || row.to_scheme || "-"}>{row.switch_in_scheme_name || row.to_scheme || "-"}</div>,
                width: "250px",
                id: "to_scheme",
            },
            {
                name: thLines('Switch Out', 'Amount'),
                selector: row => row.switch_amount,
                cell: row => <div className='line-height-20px'>{row.switch_amount || "-"}</div>,
                width: "140px",
                id: "switch_out_amount",
            },
            {
                name: th('Order Status'),
                cell: row => row.order_status ?
                    <Chip label={row.order_status}
                        className={getStatusChipClass(row.order_status)} />
                    : "-",
                width: "120px",
                id: "order_status",
            },
            {
                name: thLines('FP Order', 'Status'),
                cell: row => row.fp_order_status ?
                    <Chip label={row.fp_order_status}
                        className={getStatusChipClass(row.fp_order_status)} />
                    : "-",
                width: "130px",
                id: "fp_order_status",
            },
            {
                name: 'Rayi Order ID',
                selector: row => row.order_display_id || "-",
                cell: row => <div className='line-height-20px'>{row.order_display_id || "-"}</div>,
                width: "134px",
                sortable: true,
                sortFunction: (a, b) => a.order_id - b.order_id,
                id: "rayi_order_id",
            },
        );
    }

    if (onViewDetails) {
        columns.push({
            cell: row => (
                <div>
                    <TextButton name="View details"
                        onClick={() => onViewDetails(row)} />
                </div>
            ),
            width: "110px",
            id: "action",
        });
    }

    return columns;
};
